import { isRootMobileFieldPath, parseTenantSlugFromPath } from '@cnapse/shared/utils/app-url.js';
import { parseMobileScanStep } from './mobileScanFlow';

/** Build tenant-scoped paths for Insight Field Mode (`/m/*`). */
export function mobileFieldBasePath(subdomain: string | null): string {
  const prefix = subdomain ? `/${subdomain}` : '';
  return `${prefix}/m`;
}

export function mobileContainersPath(subdomain: string | null): string {
  return `${mobileFieldBasePath(subdomain)}/containers`;
}

export function mobileContainerDetailPath(subdomain: string | null, id: number): string {
  return `${mobileContainersPath(subdomain)}/${id}`;
}

export function mobileContainerEditPath(subdomain: string | null, id: number): string {
  return `${mobileContainerDetailPath(subdomain, id)}/edit`;
}

export function mobileWafersPath(subdomain: string | null): string {
  return `${mobileFieldBasePath(subdomain)}/wafers`;
}

export function mobileWaferDetailPath(subdomain: string | null, id: number): string {
  return `${mobileWafersPath(subdomain)}/${id}`;
}

export function mobileWaferEditPath(subdomain: string | null, id: number): string {
  return `${mobileWaferDetailPath(subdomain, id)}/edit`;
}

export function mobileWaferSlotPickerPath(subdomain: string | null, id: number): string {
  return `${mobileWaferDetailPath(subdomain, id)}/slots`;
}

export function mobileMorePath(subdomain: string | null): string {
  return `${mobileFieldBasePath(subdomain)}/more`;
}

export function mobileScanPath(subdomain: string | null, containerId?: number): string {
  const base = `${mobileFieldBasePath(subdomain)}/scan`;
  if (containerId === undefined) {
    return base;
  }
  return `${base}?containerId=${containerId}`;
}

export function mobileNodePickPath(
  subdomain: string | null,
  nodeId: string,
  containerId?: number,
): string {
  const params = new URLSearchParams({ nodeId });
  if (containerId !== undefined) {
    params.set('containerId', String(containerId));
  }
  return `${mobileFieldBasePath(subdomain)}/scan/node/pick?${params.toString()}`;
}

export function mobileNodeLinkedPath(
  subdomain: string | null,
  nodeId: string,
  containerId: number,
  containerName: string,
): string {
  const params = new URLSearchParams({
    nodeId,
    containerId: String(containerId),
    containerName,
  });
  return `${mobileFieldBasePath(subdomain)}/scan/node/linked?${params.toString()}`;
}

export function mobileNodeDonePath(
  subdomain: string | null,
  nodeId: string,
  containerId: number,
  containerName: string,
): string {
  const params = new URLSearchParams({
    nodeId,
    containerId: String(containerId),
    containerName,
  });
  return `${mobileFieldBasePath(subdomain)}/scan/node/done?${params.toString()}`;
}

export function mobileLabelPickPath(
  subdomain: string | null,
  barcode: string,
  containerId?: number,
): string {
  const params = new URLSearchParams({ barcode });
  if (containerId !== undefined) {
    params.set('containerId', String(containerId));
  }
  return `${mobileFieldBasePath(subdomain)}/scan/label/pick?${params.toString()}`;
}

export function mobileLabelDonePath(
  subdomain: string | null,
  barcode: string,
  containerId: number,
  containerName: string,
): string {
  const params = new URLSearchParams({
    barcode,
    containerId: String(containerId),
    containerName,
  });
  return `${mobileFieldBasePath(subdomain)}/scan/label/done?${params.toString()}`;
}

export function mobileLabelLinkedPath(
  subdomain: string | null,
  barcode: string,
  entityType: string,
  entityId: number,
  entityName?: string,
): string {
  const params = new URLSearchParams({
    barcode,
    entityType,
    entityId: String(entityId),
  });
  if (entityName) {
    params.set('entityName', entityName);
  }
  return `${mobileFieldBasePath(subdomain)}/scan/label/linked?${params.toString()}`;
}

export function isMobileScanFlowPath(cleanPath: string): boolean {
  return cleanPath === '/m/scan' || cleanPath.startsWith('/m/scan/');
}

const DESKTOP_INVENTORY_HOME_PATHS = new Set(['/', '/dashboard', '/containers', '/wafers']);

/**
 * Normalize legacy or malformed clean paths before route matching.
 * - `/dashboard` → `/` (desktop inventory home alias)
 * - `/dashboard/m/*` → `/m/*` (bad URLs that embed dashboard before field mode)
 */
export function normalizeAppCleanPath(cleanPath: string): string {
  if (cleanPath === '/dashboard') {
    return '/';
  }
  if (cleanPath === '/dashboard/m') {
    return '/m';
  }
  if (cleanPath.startsWith('/dashboard/m/')) {
    return cleanPath.slice('/dashboard'.length);
  }
  return cleanPath;
}

/** Desktop inventory list/home routes that mobile field mode should replace. */
export function isDesktopInventoryHomePath(cleanPath: string): boolean {
  return DESKTOP_INVENTORY_HOME_PATHS.has(normalizeAppCleanPath(cleanPath));
}

export function buildTenantPrefixedPath(subdomain: string | null, cleanPath: string): string {
  const prefix = subdomain ? `/${subdomain}` : '';
  return `${prefix}${cleanPath}`;
}

/**
 * When the browser URL embeds legacy `/dashboard` segments, return the canonical
 * pathname (including tenant prefix). Returns null when already canonical.
 */
export function canonicalizeAppPathname(pathname: string, subdomain: string | null): string | null {
  const tenant = resolveTenantSubdomain(pathname, subdomain);
  const rawClean = getMobileFieldCleanPath(pathname, tenant);
  const normalizedClean = normalizeAppCleanPath(rawClean);
  if (normalizedClean === rawClean) {
    return null;
  }
  return buildTenantPrefixedPath(tenant, normalizedClean);
}

export function getMobileFieldCleanPath(pathname: string, subdomain: string | null): string {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (isRootMobileFieldPath(normalized)) {
    return normalized;
  }
  if (subdomain && pathname.startsWith(`/${subdomain}`)) {
    return pathname.slice(`/${subdomain}`.length) || '/';
  }
  return normalized;
}

/** Tenant slug from context, or synchronously from the URL path prefix. */
export function resolveTenantSubdomain(pathname: string, subdomain: string | null): string | null {
  return subdomain ?? parseTenantSlugFromPath(pathname);
}

/** Route-matching path with tenant prefix stripped, even before async tenant context loads. */
export function resolveAppCleanPath(pathname: string, subdomain: string | null): string {
  const raw = getMobileFieldCleanPath(pathname, resolveTenantSubdomain(pathname, subdomain));
  return normalizeAppCleanPath(raw);
}

/** Detect Insight Field Mode (`/m/*`) from a full browser pathname. */
export function isMobileFieldUrl(pathname: string, subdomain: string | null = null): boolean {
  return isMobileFieldPath(resolveAppCleanPath(pathname, subdomain));
}

export type MobileFieldTab = 'containers' | 'wafers' | 'scan' | 'more';

export type MobileFieldSegment =
  | { kind: 'redirect-containers' }
  | { kind: 'containers-list' }
  | { kind: 'container-detail'; id: number }
  | { kind: 'container-edit'; id: number }
  | { kind: 'wafers-list' }
  | { kind: 'wafer-detail'; id: number }
  | { kind: 'wafer-edit'; id: number }
  | { kind: 'wafer-slots'; id: number }
  | { kind: 'scan-flow' }
  | { kind: 'more-list' };

export function parseMobileFieldSegment(cleanPath: string): MobileFieldSegment | null {
  if (cleanPath === '/m') {
    return { kind: 'redirect-containers' };
  }
  if (cleanPath === '/m/containers') {
    return { kind: 'containers-list' };
  }
  const containerEdit = cleanPath.match(/^\/m\/containers\/(\d+)\/edit$/);
  if (containerEdit) {
    return { kind: 'container-edit', id: Number(containerEdit[1]) };
  }
  const containerDetail = cleanPath.match(/^\/m\/containers\/(\d+)$/);
  if (containerDetail) {
    return { kind: 'container-detail', id: Number(containerDetail[1]) };
  }
  if (cleanPath === '/m/wafers') {
    return { kind: 'wafers-list' };
  }
  const waferSlots = cleanPath.match(/^\/m\/wafers\/(\d+)\/slots$/);
  if (waferSlots) {
    return { kind: 'wafer-slots', id: Number(waferSlots[1]) };
  }
  const waferEdit = cleanPath.match(/^\/m\/wafers\/(\d+)\/edit$/);
  if (waferEdit) {
    return { kind: 'wafer-edit', id: Number(waferEdit[1]) };
  }
  const waferDetail = cleanPath.match(/^\/m\/wafers\/(\d+)$/);
  if (waferDetail) {
    return { kind: 'wafer-detail', id: Number(waferDetail[1]) };
  }
  if (cleanPath === '/m/scan' || cleanPath.startsWith('/m/scan/')) {
    return { kind: 'scan-flow' };
  }
  if (cleanPath === '/m/more') {
    return { kind: 'more-list' };
  }
  return null;
}

/** Root tab screens show the persistent bottom nav (owned by MobileFieldAppLayout). */
export function mobileFieldShowsTabBar(
  segment: MobileFieldSegment,
  cleanPath: string,
  search: string,
): boolean {
  switch (segment.kind) {
    case 'containers-list':
    case 'wafers-list':
    case 'more-list':
      return true;
    case 'scan-flow': {
      const step = parseMobileScanStep(cleanPath, search);
      return step.kind === 'camera' && step.containerId === undefined;
    }
    default:
      return false;
  }
}

/** @deprecated Use mobileFieldShowsTabBar */
export function mobileFieldHidesBottomNav(segment: MobileFieldSegment): boolean {
  return !mobileFieldShowsTabBar(segment, '', '');
}

export function mobileFieldActiveTab(segment: MobileFieldSegment): MobileFieldTab {
  switch (segment.kind) {
    case 'wafers-list':
    case 'wafer-detail':
    case 'wafer-edit':
    case 'wafer-slots':
      return 'wafers';
    case 'scan-flow':
      return 'scan';
    case 'more-list':
      return 'more';
    default:
      return 'containers';
  }
}

/**
 * Map an Insight Field Mode path to the closest desktop inventory route when the
 * viewport is wide enough to leave mobile shell.
 */
export function desktopPathFromMobileField(cleanPath: string, search = ''): string | null {
  const segment = parseMobileFieldSegment(cleanPath);
  if (!segment) {
    return null;
  }

  const query = search.startsWith('?') ? search : search ? `?${search}` : '';

  switch (segment.kind) {
    case 'redirect-containers':
    case 'containers-list':
      return '/containers';
    case 'container-detail':
      return `/containers?view=${segment.id}`;
    case 'container-edit':
      return `/containers?edit=${segment.id}`;
    case 'wafers-list':
      return '/wafers';
    case 'wafer-detail':
    case 'wafer-edit':
    case 'wafer-slots':
      return '/wafers';
    case 'scan-flow':
      return `/labels/link/scan${query}`;
    case 'more-list':
      return '/containers';
  }
}

export function isMobileFieldPath(cleanPath: string): boolean {
  return cleanPath === '/m' || cleanPath.startsWith('/m/');
}
